import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RoleContext } from "../Contexts/RoleContext";
import {
  Container,
  Card,
  Tabs,
  Tab,
  Button,
  Badge,
  Form,
  Spinner,
  Modal,
} from "react-bootstrap";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Collapse,
  Divider,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PeopleIcon from "@mui/icons-material/People";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import PersonIcon from "@mui/icons-material/Person";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import { toast } from "react-toastify";
import PageLayout from "../Components/PageLayout";
import {
  schedulingApi,
  filterCoursesByScheduleEventType,
  courseMatchesScheduleEventType,
} from "../Components/schedulingApi";
import AttendanceModal from "../Components/AttendanceModal";
import EventAccessTable from "../Components/EventAccessTable";
import "../static/enquiry_profile.css";
import "../static/enquiry-details.css";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import listStyles from "./AllEnquiries.module.scss";
import styles from "./EventProfilePage.module.scss";

const EVENT_TYPES = ["class", "exam", "seminar", "unit"];

function parseUtcDate(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const hasTimezone = /[zZ]|[+-]\d{2}:\d{2}$/.test(raw);
  const normalized = hasTimezone ? raw : `${raw}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Monday-start week (matches unit-selection wizard). */
function startOfWeekMonday(d) {
  const x = new Date(d.getTime());
  x.setHours(0, 0, 0, 0);
  const dow = x.getDay();
  const daysFromMonday = (dow + 6) % 7;
  x.setDate(x.getDate() - daysFromMonday);
  return x;
}

/** Group scheduled rows into Week 1 … Week n by calendar week of session start. */
function weekBucketsForUnitSessions(rows) {
  if (!rows?.length) return [];
  const items = [];
  for (const s of rows) {
    const d = parseUtcDate(s.start_datetime) || (s.start_datetime ? new Date(s.start_datetime) : null);
    if (!d || Number.isNaN(d.getTime())) continue;
    items.push({ s, d });
  }
  if (!items.length) return [];
  const minMs = Math.min(...items.map((x) => x.d.getTime()));
  const anchorMs = startOfWeekMonday(new Date(minMs)).getTime();
  const msWeek = 7 * 24 * 60 * 60 * 1000;
  const weekMap = new Map();
  for (const { s, d } of items) {
    const wsMs = startOfWeekMonday(d).getTime();
    let weekNum = Math.floor((wsMs - anchorMs) / msWeek) + 1;
    if (weekNum < 1) weekNum = 1;
    if (!weekMap.has(weekNum)) weekMap.set(weekNum, []);
    weekMap.get(weekNum).push(s);
  }
  const sortedKeys = [...weekMap.keys()].sort((a, b) => a - b);
  return sortedKeys.map((weekNum) => ({
    weekNum,
    sessions: (weekMap.get(weekNum) || []).sort((a, b) =>
      String(a.start_datetime || "").localeCompare(String(b.start_datetime || ""))
    ),
  }));
}

function slotLecturerId(scheduledRow, eventRow) {
  if (scheduledRow?.lecturer_id != null && scheduledRow.lecturer_id !== "") return Number(scheduledRow.lecturer_id);
  return eventRow?.lecturer_id != null ? Number(eventRow.lecturer_id) : null;
}

function slotClassroomId(scheduledRow, eventRow) {
  if (scheduledRow?.classroom_id != null && scheduledRow.classroom_id !== "") return Number(scheduledRow.classroom_id);
  return eventRow?.classroom_id != null ? Number(eventRow.classroom_id) : null;
}

function formatSessionWhen(s) {
  const a = parseUtcDate(s.start_datetime) || (s.start_datetime ? new Date(s.start_datetime) : null);
  const b = parseUtcDate(s.end_datetime) || (s.end_datetime ? new Date(s.end_datetime) : null);
  if (!a || Number.isNaN(a.getTime())) return "—";
  const day = a.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  const t0 = a.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const t1 = b && !Number.isNaN(b.getTime()) ? b.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "";
  return t1 ? `${day} · ${t0} – ${t1}` : `${day} · ${t0}`;
}

export default function EventProfilePage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { role } = useContext(RoleContext);
  const [eventData, setEventData] = useState(null);
  const [scheduledList, setScheduledList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Details");
  const [options, setOptions] = useState({ courses: [], lecturers: [], classrooms: [] });

  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const [attendanceScheduledId, setAttendanceScheduledId] = useState(null);
  const [collapsedAttendanceGroups, setCollapsedAttendanceGroups] = useState({});
  const [attendanceByScheduledId, setAttendanceByScheduledId] = useState({});
  const [participantsSearch, setParticipantsSearch] = useState("");
  const debouncedParticipantsSearch = useDebouncedValue(participantsSearch, 400);
  const [scheduledSearch, setScheduledSearch] = useState("");
  const [rescheduleScheduled, setRescheduleScheduled] = useState(null);
  const [rescheduleStart, setRescheduleStart] = useState("");
  const [rescheduleEnd, setRescheduleEnd] = useState("");
  const [rescheduleLecturerId, setRescheduleLecturerId] = useState("");
  const [rescheduleClassroomId, setRescheduleClassroomId] = useState("");
  const [rescheduleMeetingLink, setRescheduleMeetingLink] = useState("");
  const [rescheduleIsOnline, setRescheduleIsOnline] = useState(false);
  const [rescheduleSlotCapacity, setRescheduleSlotCapacity] = useState("");
  const [rescheduleSlotMeetingLink, setRescheduleSlotMeetingLink] = useState("");
  const [rescheduleSaving, setRescheduleSaving] = useState(false);
  const [markCompleteSaving, setMarkCompleteSaving] = useState(false);
  /** Unit event — Scheduled tab: which session row has details expanded. */
  const [expandedUnitSessionId, setExpandedUnitSessionId] = useState(null);
  /** Per scheduled row: participants with SessionAllocation for that instance (unit events). */
  const [slotStudentsById, setSlotStudentsById] = useState({});
  const loadedSlotStudentsRef = useRef(new Set());
  const [attendanceModalStudents, setAttendanceModalStudents] = useState([]);
  const [attendanceModalStudentsLoading, setAttendanceModalStudentsLoading] = useState(false);
  /** Modal: ask Pass/Fail before completing an exam session (lecturer or admin). */
  const [showExamResultModal, setShowExamResultModal] = useState(false);
  const examCompleteModeRef = useRef(null); // "lecturer" | "admin" | null

  const loadOptions = useCallback(async () => {
    try {
      const data = await schedulingApi.getOptions();
      if (data?.error) return;
      setOptions({
        courses: data.courses || [],
        lecturers: data.lecturers || [],
        classrooms: data.classrooms || [],
      });
    } catch (_) {}
  }, []);

  const loadEvent = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const data = await schedulingApi.getEvent(Number(eventId));
      if (data?.error) {
        toast.error(data.error);
        setEventData(null);
        setScheduledList([]);
        return;
      }
      setEventData(data.event || null);
      setScheduledList(data.scheduled || []);
      if (data.event) setEditForm(data.event);
    } catch (e) {
      toast.error(e?.message || "Failed to load session");
      setEventData(null);
      setScheduledList([]);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  const invalidateUnitSlotRosters = useCallback(() => {
    loadedSlotStudentsRef.current = new Set();
    setSlotStudentsById({});
  }, []);

  const loadStudents = useCallback(async () => {
    if (!eventId) return;
    setStudentsLoading(true);
    try {
      const data = await schedulingApi.getEventStudents(Number(eventId));
      if (data?.error) {
        toast.error(data.error);
        setStudents([]);
        return;
      }
      setStudents(data.students || []);
    } catch (e) {
      toast.error(e?.message || "Failed to load participants");
      setStudents([]);
    } finally {
      setStudentsLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    loadEvent();
  }, [loadEvent]);

  useEffect(() => {
    if (activeTab === "Participants" && eventId) loadStudents();
  }, [activeTab, eventId, loadStudents]);

  useEffect(() => {
    setExpandedUnitSessionId(null);
    setSlotStudentsById({});
    loadedSlotStudentsRef.current = new Set();
    setAttendanceModalStudents([]);
  }, [eventId]);

  useEffect(() => {
    if (!attendanceScheduledId || !eventId) {
      setAttendanceModalStudents([]);
      setAttendanceModalStudentsLoading(false);
      return;
    }
    let cancelled = false;
    setAttendanceModalStudentsLoading(true);
    (async () => {
      try {
        const data = await schedulingApi.getEventStudents(Number(eventId), {
          scheduledId: attendanceScheduledId,
        });
        if (!cancelled && !data?.error) {
          setAttendanceModalStudents(data.students || []);
        }
      } catch (_) {
        if (!cancelled) setAttendanceModalStudents([]);
      } finally {
        if (!cancelled) setAttendanceModalStudentsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attendanceScheduledId, eventId]);

  useEffect(() => {
    const sid = expandedUnitSessionId;
    if (!sid || !eventId) return;
    if (String(eventData?.event_type || "").toLowerCase() !== "unit") return;
    if (loadedSlotStudentsRef.current.has(sid)) return;
    loadedSlotStudentsRef.current.add(sid);
    let cancelled = false;
    (async () => {
      try {
        const data = await schedulingApi.getEventStudents(Number(eventId), {
          scheduledId: sid,
        });
        if (!cancelled && !data?.error) {
          setSlotStudentsById((prev) => ({
            ...prev,
            [sid]: data.students || [],
          }));
        }
      } catch (_) {
        if (!cancelled) {
          setSlotStudentsById((prev) => ({
            ...prev,
            [sid]: [],
          }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [expandedUnitSessionId, eventId, eventData?.event_type]);

  /** Load enrolment for unit Scheduled tab (session detail lists participants). */
  useEffect(() => {
    if (
      activeTab === "Scheduled" &&
      eventId &&
      String(eventData?.event_type || "").toLowerCase() === "unit"
    ) {
      loadStudents();
    }
  }, [activeTab, eventId, eventData?.event_type, loadStudents]);

  const handleSaveEdit = async () => {
    setEditSaving(true);
    try {
      const payload = {
        title: editForm.title,
        event_type: editForm.event_type,
        course_id: editForm.course_id ? Number(editForm.course_id) : null,
        lecturer_id: editForm.lecturer_id ? Number(editForm.lecturer_id) : null,
        classroom_id: editForm.classroom_id ? Number(editForm.classroom_id) : null,
        capacity: Number(editForm.capacity) || 0,
        enrollment_required: editForm.enrollment_required || false,
        allow_waitlist: editForm.allow_waitlist || false,
        meeting_link: editForm.meeting_link || null,
        description: editForm.description || null,
      };
      const data = await schedulingApi.updateEvent(Number(eventId), payload);
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      setEventData((prev) => (prev ? { ...prev, ...payload } : null));
      setIsEditMode(false);
      toast.success("Session updated.");
    } catch (e) {
      toast.error(e?.message || "Failed to save");
    } finally {
      setEditSaving(false);
    }
  };

  const finalizeCompleteEvent = async (examResult) => {
    if (!eventId || eventData?.status !== "scheduled") return;
    const isExam = (eventData?.event_type || "").toLowerCase() === "exam";
    const payload = { status: "completed" };
    if (isExam) {
      if (!examResult) {
        toast.error("Select whether the student passed or failed.");
        return;
      }
      payload.exam_result = examResult;
    }
    setMarkCompleteSaving(true);
    try {
      const data = await schedulingApi.updateEvent(Number(eventId), payload);
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      setEventData((prev) => (prev ? { ...prev, status: "completed" } : null));
      toast.success("Session marked as completed.");
      loadEvent();
    } catch (e) {
      toast.error(e?.message || "Failed to update status");
    } finally {
      setMarkCompleteSaving(false);
      setShowExamResultModal(false);
      examCompleteModeRef.current = null;
    }
  };

  const handleMarkComplete = async () => {
    if (!eventId || eventData?.status !== "scheduled") return;
    if ((eventData?.event_type || "").toLowerCase() === "exam") {
      examCompleteModeRef.current = "lecturer";
      setShowExamResultModal(true);
      return;
    }
    await finalizeCompleteEvent(null);
  };

  const handleChangeStatus = async (newStatus, examResult = null) => {
    if (!eventId) return;
    if (
      newStatus === "completed" &&
      (eventData?.event_type || "").toLowerCase() === "exam" &&
      !examResult
    ) {
      examCompleteModeRef.current = "admin";
      setShowExamResultModal(true);
      return;
    }
    setMarkCompleteSaving(true);
    try {
      const payload = { status: newStatus };
      if (newStatus === "completed" && examResult) payload.exam_result = examResult;
      const data = await schedulingApi.updateEvent(Number(eventId), payload);
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      setEventData((prev) => (prev ? { ...prev, status: newStatus } : null));
      toast.success(`Status set to ${newStatus}.`);
      loadEvent();
    } catch (e) {
      toast.error(e?.message || "Failed to update status");
    } finally {
      setMarkCompleteSaving(false);
      setShowExamResultModal(false);
      examCompleteModeRef.current = null;
    }
  };

  const handleExamResultConfirm = async (examResult) => {
    const mode = examCompleteModeRef.current;
    if (mode === "lecturer") {
      await finalizeCompleteEvent(examResult);
      return;
    }
    if (mode === "admin") {
      await handleChangeStatus("completed", examResult);
    }
  };

  const handleRemove = async (enquiryId) => {
    if (!window.confirm("Remove this participant from the session?")) return;
    setRemovingId(enquiryId);
    try {
      const data = await schedulingApi.cancelEnrollment(Number(eventId), enquiryId);
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      toast.success("Participant removed.");
      invalidateUnitSlotRosters();
      loadStudents();
    } catch (e) {
      toast.error(e?.message || "Failed to remove");
    } finally {
      setRemovingId(null);
    }
  };

  const loadAttendanceForScheduled = useCallback(async (schedId) => {
    try {
      const data = await schedulingApi.getAttendance(schedId);
      if (data?.error) return;
      setAttendanceByScheduledId((prev) => ({ ...prev, [schedId]: data.attendance || [] }));
    } catch (_) {}
  }, []);

  const toggleAttendanceGroup = (schedId) => {
    setCollapsedAttendanceGroups((prev) => ({ ...prev, [schedId]: prev[schedId] === false }));
    if (!attendanceByScheduledId[schedId]) loadAttendanceForScheduled(schedId);
  };

  const getCourseName = (id) => (options.courses || []).find((c) => c.id === Number(id))?.name ?? "—";
  const getLecturerName = (id) => {
    const u = (options.lecturers || []).find((x) => x.id === Number(id));
    return u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || "—" : "—";
  };
  const getRoomCode = (id) => (options.classrooms || []).find((r) => r.id === Number(id))?.room_code ?? "—";

  const participantsSearchLower = (debouncedParticipantsSearch || "").trim().toLowerCase();
  const filteredParticipants = useMemo(() => {
    if (!participantsSearchLower) return students;
    return students.filter((s) => {
      const name = `${s.enquiry?.first_name || ""} ${s.enquiry?.last_name || ""}`.toLowerCase();
      const email = (s.enquiry?.email || "").toLowerCase();
      return name.includes(participantsSearchLower) || email.includes(participantsSearchLower);
    });
  }, [students, participantsSearchLower]);

  const toDatetimeLocal = (isoStr) => {
    const d = parseUtcDate(isoStr);
    if (!d) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  /** Matches scheduling_routes: direct PUT applies extras only for admin / Back Office. */
  const canEditSlotResourcesOnReschedule = role === "admin" || role === "Back Office";
  const isUnitEventType = String(eventData?.event_type || "").toLowerCase() === "unit";

  const openReschedule = (s) => {
    setRescheduleScheduled(s);
    setRescheduleStart(toDatetimeLocal(s.start_datetime));
    setRescheduleEnd(toDatetimeLocal(s.end_datetime));
    setRescheduleLecturerId(
      s.lecturer_id != null && s.lecturer_id !== "" ? String(s.lecturer_id) : ""
    );
    setRescheduleClassroomId(
      s.classroom_id != null && s.classroom_id !== "" ? String(s.classroom_id) : ""
    );
    setRescheduleMeetingLink((eventData?.meeting_link || "").trim());
    setRescheduleIsOnline(Boolean(s.is_online));
    setRescheduleSlotCapacity(
      s.slot_capacity != null && s.slot_capacity !== "" ? String(s.slot_capacity) : ""
    );
    setRescheduleSlotMeetingLink((s.slot_meeting_link || "").trim());
  };

  const handleRescheduleSave = async () => {
    if (!rescheduleScheduled || !rescheduleStart || !rescheduleEnd) return;
    const startDt = new Date(rescheduleStart);
    const endDt = new Date(rescheduleEnd);
    if (endDt <= startDt) {
      toast.error("End must be after start.");
      return;
    }
    if (
      canEditSlotResourcesOnReschedule &&
      isUnitEventType &&
      rescheduleIsOnline &&
      !(rescheduleSlotMeetingLink || "").trim()
    ) {
      toast.error("Online instances need a Zoom / meeting link for this time slot.");
      return;
    }
    if (canEditSlotResourcesOnReschedule && isUnitEventType && rescheduleSlotCapacity.trim() !== "") {
      const capN = Number(rescheduleSlotCapacity);
      if (Number.isNaN(capN) || capN < 1) {
        toast.error("Slot capacity must be a positive integer or left blank for the default.");
        return;
      }
    }
    setRescheduleSaving(true);
    try {
      const payload = {
        start_datetime: startDt.toISOString(),
        end_datetime: endDt.toISOString(),
      };
      if (canEditSlotResourcesOnReschedule) {
        payload.lecturer_id = rescheduleLecturerId === "" ? null : Number(rescheduleLecturerId);
        payload.meeting_link = rescheduleMeetingLink.trim() || null;
        if (isUnitEventType) {
          payload.is_online = rescheduleIsOnline;
          payload.slot_capacity = rescheduleSlotCapacity.trim() === "" ? null : Number(rescheduleSlotCapacity);
          payload.slot_meeting_link = rescheduleIsOnline
            ? (rescheduleSlotMeetingLink.trim() || null)
            : null;
          payload.classroom_id = rescheduleIsOnline
            ? null
            : rescheduleClassroomId === "" ? null : Number(rescheduleClassroomId);
        } else {
          payload.classroom_id = rescheduleClassroomId === "" ? null : Number(rescheduleClassroomId);
        }
      }
      const data = await schedulingApi.updateEventScheduled(rescheduleScheduled.id, payload);
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      toast.success(data.message || "Session rescheduled.");
      setRescheduleScheduled(null);
      loadEvent();
    } catch (e) {
      toast.error(e?.message || "Failed to reschedule");
    } finally {
      setRescheduleSaving(false);
    }
  };

  const scheduledSearchLower = (scheduledSearch || "").trim().toLowerCase();
  const filteredScheduled = useMemo(() => {
    if (!scheduledSearchLower) return scheduledList;
    return scheduledList.filter((s) => {
      const start = (s.start_datetime || "").toString().toLowerCase();
      const end = (s.end_datetime || "").toString().toLowerCase();
      return start.includes(scheduledSearchLower) || end.includes(scheduledSearchLower);
    });
  }, [scheduledList, scheduledSearchLower]);

  const courseDetail = useMemo(
    () => (options.courses || []).find((c) => c.id === Number(eventData?.course_id)),
    [options.courses, eventData?.course_id]
  );

  /** Course dropdown in edit mode — was missing and caused `.map` on undefined when editing. */
  const editCoursesFiltered = useMemo(
    () =>
      filterCoursesByScheduleEventType(
        options.courses || [],
        editForm?.event_type || eventData?.event_type || "class"
      ),
    [options.courses, editForm?.event_type, eventData?.event_type]
  );

  const unitPartLabel = useCallback(
    (componentIndex) => {
      if (componentIndex == null || componentIndex === "" || componentIndex === "_other") {
        return "Unassigned part";
      }
      const idx = Number(componentIndex);
      if (!Number.isFinite(idx)) return "Unassigned part";
      const comp = courseDetail?.session_components?.[idx];
      return comp?.session_type || comp?.sessionType || `Session part ${idx + 1}`;
    },
    [courseDetail]
  );

  /** For unit events, one table per catalogue part (unit_component_index); otherwise a single group. */
  const scheduledTableGroups = useMemo(() => {
    const isUnit = String(eventData?.event_type || "").toLowerCase() === "unit";
    const list = filteredScheduled;
    if (!isUnit) {
      return [{ key: "all", label: null, rows: list }];
    }
    if (!list.length) {
      return [{ key: "empty", label: null, rows: [] }];
    }
    const map = new Map();
    for (const s of list) {
      const raw = s.unit_component_index;
      const key = raw != null && raw !== "" ? Number(raw) : "_other";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    const keys = [...map.keys()].sort((a, b) => {
      if (a === "_other") return 1;
      if (b === "_other") return -1;
      return Number(a) - Number(b);
    });
    return keys.map((k) => ({
      key: String(k),
      label: k === "_other" ? "Unassigned part" : unitPartLabel(k),
      rows: (map.get(k) || []).slice().sort((a, b) => {
        return String(a.start_datetime || "").localeCompare(String(b.start_datetime || ""));
      }),
    }));
  }, [filteredScheduled, eventData?.event_type, unitPartLabel]);

  const unitScheduledWeeks = useMemo(() => {
    if (String(eventData?.event_type || "").toLowerCase() !== "unit") return null;
    return weekBucketsForUnitSessions(filteredScheduled);
  }, [eventData?.event_type, filteredScheduled]);

  const attendanceGroupsForUnit = useMemo(() => {
    const isUnit = String(eventData?.event_type || "").toLowerCase() === "unit";
    const list = scheduledList;
    if (!isUnit) {
      return [{ key: "all", label: null, rows: list }];
    }
    if (!list.length) {
      return [{ key: "empty", label: null, rows: [] }];
    }
    const map = new Map();
    for (const s of list) {
      const raw = s.unit_component_index;
      const key = raw != null && raw !== "" ? Number(raw) : "_other";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    const keys = [...map.keys()].sort((a, b) => {
      if (a === "_other") return 1;
      if (b === "_other") return -1;
      return Number(a) - Number(b);
    });
    return keys.map((k) => ({
      key: String(k),
      label: k === "_other" ? "Unassigned part" : unitPartLabel(k),
      rows: (map.get(k) || []).slice().sort((a, b) =>
        String(a.start_datetime || "").localeCompare(String(b.start_datetime || ""))
      ),
    }));
  }, [scheduledList, eventData?.event_type, unitPartLabel]);

  if (!eventId) {
    return (
      <Container fluid className="enquiry-profile-page p-0">
        <PageLayout title="Session Profile" subtitle="View and manage session details." backTo="/schedule/events">
            <Card className="enquiry-dashboard-card">
              <Card.Body>Invalid session.</Card.Body>
            </Card>
        </PageLayout>
      </Container>
    );
  }

  if (loading || !eventData) {
    return (
      <Container fluid className="enquiry-profile-page p-0">
        <PageLayout title="Session Profile" subtitle="View and manage session details." backTo="/schedule/events">
            <Card className="enquiry-dashboard-card">
              <Card.Body className="text-center py-5">
                {loading ? <Spinner animation="border" /> : "Session not found."}
              </Card.Body>
            </Card>
        </PageLayout>
      </Container>
    );
  }

  return (
    <Container fluid className="enquiry-profile-page p-0">
      <PageLayout title="Session Profile" subtitle="View and manage session details." backTo="/schedule/events">
          <div className="enquiry-dashboard-container">
            <Card className="enquiry-dashboard-card enquiry-dashboard-card--unassigned">
              <Card.Body>
                <div className="dashboard-header-top">
                  <Button variant="link" className="back-to-list-btn" onClick={() => navigate("/schedule/events")}>
                    <ArrowBackIcon fontSize="small" />
                    <span>Back to Sessions</span>
                  </Button>
                  <div className="dashboard-header-badges">
                    <Badge bg="light" text="dark" className="current-status-badge text-capitalize">
                      {eventData.status}
                    </Badge>
                    <Badge bg="light" text="dark" className="current-status-badge text-capitalize">
                      {eventData.event_type}
                    </Badge>
                  </div>
                </div>
                <div className="dashboard-profile">
                  <div className="dashboard-avatar" style={{ fontSize: "1.5rem" }}>
                    {eventData.title ? eventData.title.charAt(0).toUpperCase() : "E"}
                  </div>
                  <div className="dashboard-profile-info">
                    <h2>{eventData.title || "Untitled Session"}</h2>
                    <p>
                      {getCourseName(eventData.course_id)} · {getRoomCode(eventData.classroom_id)}
                    </p>
                    <div className="dashboard-meta">
                      <span>Lecturer: {getLecturerName(eventData.lecturer_id)}</span>
                      <span>Capacity: {eventData.capacity}</span>
                      <span>Sessions: {scheduledList.length}</span>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>

            <Card className="enquiry-dashboard-card tabs-card">
              <Card.Body className="tabs-body">
                <Tabs
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k || "Details")}
                  id="event-dashboard-tabs"
                  className="custom-tabs mb-3"
                >
                  <Tab
                    eventKey="Details"
                    title={
                      <span className="tab-title">
                        <DescriptionOutlinedIcon fontSize="small" /> Details
                      </span>
                    }
                  >
                    <div className="enquiry-tab-panel">
                      {!isEditMode ? (
                        <div className="enquiry-details-layout">
                          <div className="enquiry-main-column">
                            <Card className="enquiry-card">
                              <Card.Body>
                                <h5 className="section-title">Overview</h5>
                                <p className="section-muted">
                                  <strong>Title:</strong> {eventData.title}
                                </p>
                                <p className="section-muted">
                                  <strong>Type:</strong> <span className="text-capitalize">{eventData.event_type}</span>
                                  {eventData.status && (
                                    <> · <strong>Status:</strong> <span className="text-capitalize">{eventData.status}</span></>
                                  )}
                                </p>
                              </Card.Body>
                            </Card>
                            <Card className="enquiry-card">
                              <Card.Body>
                                <h5 className="section-title">Location & capacity</h5>
                                <p className="section-muted">
                                  <strong>Course:</strong> {getCourseName(eventData.course_id)}
                                </p>
                                <p className="section-muted">
                                  <strong>Lecturer:</strong> {getLecturerName(eventData.lecturer_id)}
                                </p>
                                <p className="section-muted">
                                  <strong>Room:</strong> {getRoomCode(eventData.classroom_id)}
                                </p>
                                <p className="section-muted">
                                  <strong>Capacity:</strong> {eventData.capacity}
                                </p>
                              </Card.Body>
                            </Card>
                            <Card className="enquiry-card">
                              <Card.Body>
                                <h5 className="section-title">Settings</h5>
                                <p className="section-muted">
                                  <strong>Enrollment required:</strong> {eventData.enrollment_required ? "Yes" : "No"}
                                </p>
                                <p className="section-muted">
                                  <strong>Allow waitlist:</strong> {eventData.allow_waitlist ? "Yes" : "No"}
                                </p>
                                {eventData.description && (
                                  <p className="section-muted mt-2">
                                    <strong>Description:</strong><br />
                                    {eventData.description}
                                  </p>
                                )}
                              </Card.Body>
                            </Card>
                          </div>
                          <div className="enquiry-right-column">
                            <Card className="enquiry-card right-card enquiry-actions-card">
                              <Card.Body>
                                <h6 className="section-title">Actions</h6>
                                <div className="enquiry-actions-buttons">
                                  {role === "Lecturer" && eventData.status === "scheduled" && (
                                    <Button
                                      variant="success"
                                      size="sm"
                                      type="button"
                                      className="w-100"
                                      onClick={handleMarkComplete}
                                      disabled={markCompleteSaving}
                                    >
                                      {markCompleteSaving ? <Spinner animation="border" size="sm" className="me-1" /> : null}
                                      Mark as completed
                                    </Button>
                                  )}
                                  {role !== "Lecturer" && (
                                    <>
                                      <Form.Select
                                        size="sm"
                                        className="w-100"
                                        value={eventData.status || ""}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          if (v && v !== eventData.status) handleChangeStatus(v);
                                        }}
                                        disabled={markCompleteSaving}
                                      >
                                        <option value="scheduled">Scheduled</option>
                                        <option value="pending_approval">Pending approval</option>
                                        <option value="completed">Completed</option>
                                        <option value="cancelled">Cancelled</option>
                                      </Form.Select>
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        type="button"
                                        className="w-100"
                                        onClick={() => setIsEditMode(true)}
                                      >
                                        <EditIcon fontSize="small" className="me-1" />
                                        Edit
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </Card.Body>
                            </Card>
                          </div>
                        </div>
                      ) : (
                        <>
                          <Form>
                            <Form.Group className="mb-2">
                              <Form.Label>Title</Form.Label>
                              <Form.Control
                                value={editForm.title || ""}
                                onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                              />
                            </Form.Group>
                            <Form.Group className="mb-2">
                              <Form.Label>Type</Form.Label>
                              <Form.Select
                                value={editForm.event_type || "class"}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setEditForm((f) => {
                                    const next = { ...f, event_type: v };
                                    const course = (options.courses || []).find((c) => c.id === Number(f.course_id));
                                    if (course && !courseMatchesScheduleEventType(course, v)) {
                                      next.course_id = null;
                                    }
                                    return next;
                                  });
                                }}
                              >
                                {EVENT_TYPES.map((t) => (
                                  <option key={t} value={t}>
                                    {t.charAt(0).toUpperCase() + t.slice(1)}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                            <div className="row">
                              <div className="col-md-6">
                                <Form.Group className="mb-2">
                                  <Form.Label>Course</Form.Label>
                                  <Form.Select
                                    value={editForm.course_id ?? ""}
                                    onChange={(e) =>
                                      setEditForm((f) => ({ ...f, course_id: e.target.value ? Number(e.target.value) : null }))
                                    }
                                  >
                                    <option value="">—</option>
                                    {editCoursesFiltered.map((c) => (
                                      <option key={c.id} value={c.id}>
                                        {c.name}
                                      </option>
                                    ))}
                                  </Form.Select>
                                  {editCoursesFiltered.length === 0 && (
                                    <Form.Text className="text-warning small">No courses for this type.</Form.Text>
                                  )}
                                </Form.Group>
                              </div>
                              <div className="col-md-6">
                                <Form.Group className="mb-2">
                                  <Form.Label>Lecturer</Form.Label>
                                  <Form.Select
                                    value={editForm.lecturer_id ?? ""}
                                    onChange={(e) =>
                                      setEditForm((f) => ({
                                        ...f,
                                        lecturer_id: e.target.value ? Number(e.target.value) : null,
                                      }))
                                    }
                                  >
                                    <option value="">—</option>
                                    {(options.lecturers || []).map((u) => (
                                      <option key={u.id} value={u.id}>
                                        {u.first_name} {u.last_name}
                                      </option>
                                    ))}
                                  </Form.Select>
                                </Form.Group>
                              </div>
                            </div>
                            <Form.Group className="mb-2">
                              <Form.Label>Classroom</Form.Label>
                              <Form.Select
                                value={editForm.classroom_id ?? ""}
                                onChange={(e) =>
                                  setEditForm((f) => ({
                                    ...f,
                                    classroom_id: e.target.value ? Number(e.target.value) : null,
                                  }))
                                }
                              >
                                <option value="">—</option>
                                {(options.classrooms || []).map((r) => (
                                  <option key={r.id} value={r.id}>
                                    {r.room_code} ({r.capacity} seats)
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-2">
                              <Form.Label>Capacity</Form.Label>
                              <Form.Control
                                type="number"
                                min={0}
                                value={editForm.capacity ?? 0}
                                onChange={(e) => setEditForm((f) => ({ ...f, capacity: Number(e.target.value) || 0 }))}
                              />
                            </Form.Group>
                            <Form.Group className="mb-2">
                              <Form.Check
                                type="checkbox"
                                label="Enrollment required"
                                checked={editForm.enrollment_required || false}
                                onChange={(e) => setEditForm((f) => ({ ...f, enrollment_required: e.target.checked }))}
                              />
                            </Form.Group>
                            <Form.Group className="mb-2">
                              <Form.Check
                                type="checkbox"
                                label="Allow waitlist"
                                checked={editForm.allow_waitlist || false}
                                onChange={(e) => setEditForm((f) => ({ ...f, allow_waitlist: e.target.checked }))}
                              />
                            </Form.Group>
                            <Form.Group className="mb-2">
                              <Form.Label>Description</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                value={editForm.description || ""}
                                onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                              />
                            </Form.Group>
                            <div className="d-flex gap-2">
                              <Button variant="primary" onClick={handleSaveEdit} disabled={editSaving}>
                                {editSaving ? <Spinner animation="border" size="sm" className="me-1" /> : <SaveIcon fontSize="small" className="me-1" />}
                                Save
                              </Button>
                              <Button variant="outline-secondary" onClick={() => setIsEditMode(false)}>
                                Cancel
                              </Button>
                            </div>
                          </Form>
                        </>
                      )}
                    </div>
                  </Tab>

                  <Tab
                    eventKey="Participants"
                    title={
                      <span className="tab-title">
                        <PeopleIcon fontSize="small" /> Participants
                      </span>
                    }
                  >
                    <div className="enquiry-tab-panel">
                      <Box className={listStyles.header} sx={{ mb: 2 }}>
                        <TextField
                          size="small"
                          placeholder="Search by name or email…"
                          value={participantsSearch}
                          onChange={(e) => setParticipantsSearch(e.target.value)}
                          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "action.active" }} /> }}
                          sx={{ maxWidth: 320 }}
                        />
                      </Box>
                      {studentsLoading ? (
                        <Box sx={{ py: 3, textAlign: "center" }}>
                          <Spinner animation="border" size="sm" />
                        </Box>
                      ) : (
                        <Paper variant="outlined" className={listStyles.tableContainer}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Name</strong></TableCell>
                                <TableCell><strong>Email</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                {eventData.status !== "cancelled" && eventData.status !== "rejected" && (
                                  <TableCell align="right"><strong>Actions</strong></TableCell>
                                )}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredParticipants.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={4} className="text-muted">
                                    No participants yet.
                                  </TableCell>
                                </TableRow>
                              ) : (
                                filteredParticipants.map((s) => {
                                  const eid = s.enquiry_id ?? s.enquiry?.id;
                                  return (
                                    <TableRow key={s.id} className={listStyles.tableRow}>
                                      <TableCell>
                                        <Typography variant="body2" className={listStyles.primaryText}>
                                          {s.enquiry?.first_name} {s.enquiry?.last_name}
                                        </Typography>
                                      </TableCell>
                                      <TableCell className={listStyles.secondaryText}>{s.enquiry?.email}</TableCell>
                                      <TableCell>
                                        <Badge
                                          bg={
                                            s.status === "granted" || s.status === "valid"
                                              ? "success"
                                              : s.status === "already_used" || s.status === "used"
                                              ? "secondary"
                                              : s.status === "expired" || s.status === "invalid"
                                              ? "danger"
                                              : "warning"
                                          }
                                          className="text-capitalize"
                                        >
                                          {s.status || "—"}
                                        </Badge>
                                      </TableCell>
                                      {eventData.status !== "cancelled" && eventData.status !== "rejected" && (
                                        <TableCell align="right">
                                          <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleRemove(eid)}
                                            disabled={removingId === eid}
                                          >
                                            {removingId === eid ? <Spinner animation="border" size="sm" /> : "Remove"}
                                          </Button>
                                        </TableCell>
                                      )}
                                    </TableRow>
                                  );
                                })
                              )}
                            </TableBody>
                          </Table>
                        </Paper>
                      )}
                      {!studentsLoading && filteredParticipants.length > 0 && (
                        <Box className={listStyles.pagination} sx={{ mt: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Showing {filteredParticipants.length} participant{filteredParticipants.length !== 1 ? "s" : ""}
                          </Typography>
                        </Box>
                      )}
                    </div>
                  </Tab>

                  <Tab
                    eventKey="Access Control"
                    title={
                      <span className="tab-title">
                        <QrCode2Icon fontSize="small" /> Access Control
                      </span>
                    }
                  >
                    <div className="enquiry-tab-panel">
                      <EventAccessTable
                        eventId={Number(eventId)}
                      />
                    </div>
                  </Tab>

                  <Tab
                    eventKey="Attendance"
                    title={
                      <span className="tab-title">
                        <EventAvailableIcon fontSize="small" /> Attendance
                      </span>
                    }
                  >
                    <div className={`enquiry-tab-panel ${styles.attendanceTab}`}>
                      <div className={styles.attendanceTopbar}>
                        <Typography variant="body2" color="text.secondary">
                          Expand a session to view or mark attendance.
                        </Typography>
                      </div>
                      {scheduledList.length === 0 ? (
                        <div className={styles.attendanceState}>No scheduled sessions.</div>
                      ) : (
                        attendanceGroupsForUnit.map((group) => (
                          <Box key={`att-${group.key}`} sx={{ mb: 2 }}>
                            {group.label ? (
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.25 }}>
                                {group.label}
                              </Typography>
                            ) : null}
                            {group.rows.map((s) => {
                              const isCollapsed = collapsedAttendanceGroups[s.id] !== false;
                              const attendanceList = attendanceByScheduledId[s.id] || [];
                              return (
                                <section key={s.id} className={styles.attendanceGroup}>
                                  <header
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => toggleAttendanceGroup(s.id)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        toggleAttendanceGroup(s.id);
                                      }
                                    }}
                                  >
                                    <div className={styles.attendanceGroupTitleWrap}>
                                      <span className={`${styles.attendanceGroupArrow} ${isCollapsed ? styles.collapsed : ""}`}>▾</span>
                                      <h5>
                                        {s.start_datetime ? (parseUtcDate(s.start_datetime) || new Date(s.start_datetime)).toLocaleString() : "—"}
                                        {s.end_datetime
                                          ? ` – ${(parseUtcDate(s.end_datetime) || new Date(s.end_datetime)).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`
                                          : ""}
                                      </h5>
                                    </div>
                                    <span>{attendanceList.length} recorded</span>
                                  </header>
                                  {!isCollapsed && (
                                    <div className={styles.attendanceGroupContent}>
                                      <div className={styles.attendanceActions}>
                                        <Button
                                          size="sm"
                                          variant="primary"
                                          onClick={() => setAttendanceScheduledId(s.id)}
                                        >
                                          Mark attendance
                                        </Button>
                                      </div>
                                      {attendanceList.length === 0 ? (
                                        <p className="text-muted small mb-0">No attendance recorded for this session.</p>
                                      ) : (
                                        <Table size="sm" bordered>
                                          <thead>
                                            <tr>
                                              <th>Student</th>
                                              <th>Status</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {attendanceList.map((a) => (
                                              <tr key={a.id}>
                                                <td>
                                                  {a.enquiry?.first_name} {a.enquiry?.last_name}
                                                  {a.enquiry?.email && ` (${a.enquiry.email})`}
                                                </td>
                                                <td className="text-capitalize">{a.status}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </Table>
                                      )}
                                    </div>
                                  )}
                                </section>
                              );
                            })}
                          </Box>
                        ))
                      )}
                    </div>
                  </Tab>

                  <Tab
                    eventKey="Scheduled"
                    title={
                      <span className="tab-title">
                        <CalendarMonthIcon fontSize="small" /> Scheduled classes
                      </span>
                    }
                  >
                    <div className="enquiry-tab-panel">
                      <Box className={listStyles.header} sx={{ mb: 2 }}>
                        <TextField
                          size="small"
                          placeholder="Search by date or time…"
                          value={scheduledSearch}
                          onChange={(e) => setScheduledSearch(e.target.value)}
                          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "action.active" }} /> }}
                          sx={{ maxWidth: 320 }}
                        />
                      </Box>
                      {unitScheduledWeeks ? (
                        <>
                          {unitScheduledWeeks.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              {filteredScheduled.length === 0 && scheduledList.length > 0
                                ? "No sessions match your search."
                                : "No scheduled sessions yet."}
                            </Typography>
                          ) : (
                            unitScheduledWeeks.map(({ weekNum, sessions }) => (
                              <Accordion
                                key={weekNum}
                                defaultExpanded={weekNum === 1}
                                disableGutters
                                elevation={0}
                                sx={{
                                  mb: 1.5,
                                  border: 1,
                                  borderColor: "divider",
                                  borderRadius: 1,
                                  overflow: "hidden",
                                  "&:before": { display: "none" },
                                }}
                              >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Typography component="span" fontWeight={700}>
                                    Week {weekNum}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={`${sessions.length} session${sessions.length !== 1 ? "s" : ""}`}
                                    sx={{ ml: 1.5 }}
                                  />
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                                  <Stack spacing={1.25}>
                                    {sessions.map((s) => {
                                      const open = expandedUnitSessionId === s.id;
                                      const canReschedule =
                                        eventData?.status !== "cancelled" && eventData?.status !== "rejected";
                                      const meetingLink = (
                                        s.slot_meeting_link ||
                                        eventData?.meeting_link ||
                                        ""
                                      ).trim();
                                      const roomId = s.is_online ? null : slotClassroomId(s, eventData);
                                      const lecId = slotLecturerId(s, eventData);
                                      return (
                                        <Paper key={s.id} variant="outlined" sx={{ overflow: "hidden" }}>
                                          <Box
                                            role="button"
                                            tabIndex={0}
                                            onClick={() =>
                                              setExpandedUnitSessionId((id) => (id === s.id ? null : s.id))
                                            }
                                            onKeyDown={(e) => {
                                              if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                setExpandedUnitSessionId((id) => (id === s.id ? null : s.id));
                                              }
                                            }}
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                              p: 1.5,
                                              cursor: "pointer",
                                              "&:hover": { bgcolor: "action.hover" },
                                            }}
                                          >
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                              <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                                                <Chip
                                                  size="small"
                                                  label={unitPartLabel(s.unit_component_index)}
                                                  color="primary"
                                                  variant="outlined"
                                                />
                                                <Typography variant="body2" color="text.secondary">
                                                  {formatSessionWhen(s)}
                                                </Typography>
                                                {s.is_past ? (
                                                  <Badge bg="success">Completed</Badge>
                                                ) : (
                                                  <Badge bg="secondary">Upcoming</Badge>
                                                )}
                                                {s.is_online ? (
                                                  <Chip size="small" label="Online" color="info" variant="outlined" />
                                                ) : null}
                                              </Stack>
                                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                {s.duration_minutes != null ? `${s.duration_minutes} min` : ""}
                                                {s.duration_minutes != null ? " · " : ""}
                                                {s.effective_capacity != null
                                                  ? `cap ${s.effective_capacity}${s.allocation_count != null ? ` · ${s.allocation_count} allocated` : ""}`
                                                  : ""}
                                                {s.effective_capacity != null ? " · " : ""}
                                                Click for details
                                              </Typography>
                                            </Box>
                                            <ExpandMoreIcon
                                              sx={{
                                                color: "action.active",
                                                transform: open ? "rotate(180deg)" : "none",
                                                transition: "transform 0.2s",
                                              }}
                                            />
                                          </Box>
                                          <Collapse in={open}>
                                            <Divider />
                                            <Stack spacing={1.5} sx={{ p: 2 }}>
                                              <div>
                                                <Typography variant="subtitle2" gutterBottom>
                                                  Participants
                                                </Typography>
                                                {slotStudentsById[s.id] === undefined ? (
                                                  <Typography variant="body2" color="text.secondary">
                                                    Loading…
                                                  </Typography>
                                                ) : slotStudentsById[s.id].length === 0 ? (
                                                  <Typography variant="body2" color="text.secondary">
                                                    No students allocated to this session instance.
                                                  </Typography>
                                                ) : (
                                                  <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5, mb: 0 }}>
                                                    {slotStudentsById[s.id].map((row) => (
                                                      <Typography
                                                        key={row.id ?? `${row.enquiry?.id}-${row.enquiry?.email}`}
                                                        component="li"
                                                        variant="body2"
                                                      >
                                                        {row.enquiry?.first_name} {row.enquiry?.last_name}
                                                        {row.enquiry?.email ? ` (${row.enquiry.email})` : ""}
                                                      </Typography>
                                                    ))}
                                                  </Stack>
                                                )}
                                              </div>
                                              <Stack direction="row" alignItems="flex-start" spacing={1}>
                                                <MeetingRoomIcon sx={{ fontSize: 20, color: "action.active", mt: 0.25 }} />
                                                <div>
                                                  <Typography variant="caption" color="text.secondary">
                                                    Room
                                                  </Typography>
                                                  <Typography variant="body2">
                                                    {s.is_online ? "Online (no room)" : getRoomCode(roomId)}
                                                  </Typography>
                                                </div>
                                              </Stack>
                                              <Stack direction="row" alignItems="flex-start" spacing={1}>
                                                <PersonIcon sx={{ fontSize: 20, color: "action.active", mt: 0.25 }} />
                                                <div>
                                                  <Typography variant="caption" color="text.secondary">
                                                    Lecturer
                                                  </Typography>
                                                  <Typography variant="body2">{getLecturerName(lecId)}</Typography>
                                                </div>
                                              </Stack>
                                              {meetingLink ? (
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                  <VideoCallIcon sx={{ fontSize: 20, color: "action.active" }} />
                                                  <Link href={meetingLink} target="_blank" rel="noopener noreferrer">
                                                    {s.is_online ? "Open Zoom / meeting link" : "Event meeting link"}
                                                  </Link>
                                                </Stack>
                                              ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                  {s.is_online ? "No meeting link set for this slot." : "No meeting link on this event."}
                                                </Typography>
                                              )}
                                              {canReschedule && (
                                                <Box>
                                                  <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      openReschedule(s);
                                                    }}
                                                  >
                                                    <ScheduleIcon fontSize="small" className="me-1" />
                                                    Reschedule
                                                  </Button>
                                                </Box>
                                              )}
                                            </Stack>
                                          </Collapse>
                                        </Paper>
                                      );
                                    })}
                                  </Stack>
                                </AccordionDetails>
                              </Accordion>
                            ))
                          )}
                          {filteredScheduled.length > 0 && (
                            <Box className={listStyles.pagination} sx={{ mt: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Showing {filteredScheduled.length} session{filteredScheduled.length !== 1 ? "s" : ""}
                              </Typography>
                            </Box>
                          )}
                        </>
                      ) : (
                        <>
                          {scheduledTableGroups.map((group) => {
                            const colSpan =
                              eventData?.status !== "cancelled" && eventData?.status !== "rejected" ? 6 : 5;
                            return (
                              <Box key={group.key} sx={{ mb: 3 }}>
                                {group.label ? (
                                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                                    {group.label}
                                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                                      ({group.rows.length} slot{group.rows.length !== 1 ? "s" : ""})
                                    </Typography>
                                  </Typography>
                                ) : null}
                                <Paper variant="outlined" className={listStyles.tableContainer}>
                                  <Table size="small" stickyHeader>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell><strong>#</strong></TableCell>
                                        <TableCell><strong>Start</strong></TableCell>
                                        <TableCell><strong>End</strong></TableCell>
                                        <TableCell><strong>Duration</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                        {eventData?.status !== "cancelled" && eventData?.status !== "rejected" && (
                                          <TableCell align="right"><strong>Actions</strong></TableCell>
                                        )}
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {group.rows.length === 0 ? (
                                        <TableRow>
                                          <TableCell colSpan={colSpan} className="text-muted">
                                            No scheduled classes in this part.
                                          </TableCell>
                                        </TableRow>
                                      ) : (
                                        group.rows.map((s, idx) => (
                                          <TableRow key={s.id} className={listStyles.tableRow}>
                                            <TableCell className={listStyles.primaryText}>{idx + 1}</TableCell>
                                            <TableCell>
                                              {s.start_datetime
                                                ? (parseUtcDate(s.start_datetime) || new Date(s.start_datetime)).toLocaleString()
                                                : "—"}
                                            </TableCell>
                                            <TableCell>
                                              {s.end_datetime
                                                ? (parseUtcDate(s.end_datetime) || new Date(s.end_datetime)).toLocaleString()
                                                : "—"}
                                            </TableCell>
                                            <TableCell>{s.duration_minutes} min</TableCell>
                                            <TableCell>
                                              {s.is_past ? (
                                                <Badge bg="success">Completed</Badge>
                                              ) : (
                                                <Badge bg="secondary">Upcoming</Badge>
                                              )}
                                            </TableCell>
                                            {eventData?.status !== "cancelled" && eventData?.status !== "rejected" && (
                                              <TableCell align="right">
                                                <Button
                                                  variant="outline-primary"
                                                  size="sm"
                                                  onClick={() => openReschedule(s)}
                                                >
                                                  <ScheduleIcon fontSize="small" className="me-1" />
                                                  Reschedule
                                                </Button>
                                              </TableCell>
                                            )}
                                          </TableRow>
                                        ))
                                      )}
                                    </TableBody>
                                  </Table>
                                </Paper>
                              </Box>
                            );
                          })}
                          {filteredScheduled.length > 0 && (
                            <Box className={listStyles.pagination} sx={{ mt: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Showing {filteredScheduled.length} session{filteredScheduled.length !== 1 ? "s" : ""}
                              </Typography>
                            </Box>
                          )}
                        </>
                      )}
                    </div>
                  </Tab>
                </Tabs>
              </Card.Body>
            </Card>
          </div>
      </PageLayout>

      <AttendanceModal
        show={Boolean(attendanceScheduledId)}
        onHide={() => setAttendanceScheduledId(null)}
        scheduledId={attendanceScheduledId}
        students={attendanceModalStudents}
        studentsLoading={attendanceModalStudentsLoading}
        onSaved={() => {
          if (attendanceScheduledId) loadAttendanceForScheduled(attendanceScheduledId);
          setAttendanceScheduledId(null);
        }}
      />

      <Modal
        show={showExamResultModal}
        onHide={() => {
          if (markCompleteSaving) return;
          setShowExamResultModal(false);
          examCompleteModeRef.current = null;
        }}
        centered
        backdrop={markCompleteSaving ? "static" : true}
      >
        <Modal.Header closeButton>
          <Modal.Title>Exam completed</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">This session is an exam. Did the student pass or fail?</p>
          {markCompleteSaving && (
            <div className="text-center mt-3">
              <Spinner animation="border" size="sm" role="status" />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex flex-wrap gap-2 justify-content-end">
          <Button
            variant="secondary"
            disabled={markCompleteSaving}
            onClick={() => {
              setShowExamResultModal(false);
              examCompleteModeRef.current = null;
            }}
          >
            Cancel
          </Button>
          <Button variant="danger" disabled={markCompleteSaving} onClick={() => handleExamResultConfirm("fail")}>
            Failed
          </Button>
          <Button variant="success" disabled={markCompleteSaving} onClick={() => handleExamResultConfirm("pass")}>
            Passed
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(rescheduleScheduled)}
        onHide={() => !rescheduleSaving && setRescheduleScheduled(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Reschedule session</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {rescheduleScheduled &&
            String(eventData?.event_type || "").toLowerCase() === "unit" &&
            (rescheduleScheduled.unit_component_index != null && rescheduleScheduled.unit_component_index !== "") && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Part: <strong>{unitPartLabel(Number(rescheduleScheduled.unit_component_index))}</strong>
              </Typography>
            )}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Start</Form.Label>
              <Form.Control
                type="datetime-local"
                value={rescheduleStart}
                onChange={(e) => setRescheduleStart(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>End</Form.Label>
              <Form.Control
                type="datetime-local"
                value={rescheduleEnd}
                onChange={(e) => setRescheduleEnd(e.target.value)}
              />
            </Form.Group>
            {canEditSlotResourcesOnReschedule && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Lecturer for this occurrence</Form.Label>
                  <Form.Select
                    value={rescheduleLecturerId}
                    onChange={(e) => setRescheduleLecturerId(e.target.value)}
                  >
                    <option value="">
                      Use event default
                      {eventData?.lecturer_id != null && eventData?.lecturer_id !== ""
                        ? ` (${getLecturerName(eventData.lecturer_id)})`
                        : ""}
                    </option>
                    {(options.lecturers || []).map((u) => (
                      <option key={u.id} value={String(u.id)}>
                        {[u.first_name, u.last_name].filter(Boolean).join(" ") ||
                          u.username ||
                          `User ${u.id}`}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                {isUnitEventType && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        id="reschedule-slot-online"
                        label="This occurrence is online (Zoom) instead of a room"
                        checked={rescheduleIsOnline}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setRescheduleIsOnline(on);
                          if (on) setRescheduleClassroomId("");
                        }}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Capacity this slot{" "}
                        <span className="text-muted small">(optional)</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        placeholder={
                          rescheduleIsOnline
                            ? "Default: event capacity"
                            : "Default: room capacity"
                        }
                        value={rescheduleSlotCapacity}
                        onChange={(e) => setRescheduleSlotCapacity(e.target.value)}
                      />
                      <Form.Text className="text-muted">
                        Leave blank to use the room seat count or, for online, the event capacity. Session allocations cannot exceed this.
                      </Form.Text>
                    </Form.Group>
                    {rescheduleIsOnline ? (
                      <Form.Group className="mb-3">
                        <Form.Label>Zoom / meeting link for this slot *</Form.Label>
                        <Form.Control
                          type="url"
                          placeholder="https://…"
                          value={rescheduleSlotMeetingLink}
                          onChange={(e) => setRescheduleSlotMeetingLink(e.target.value)}
                        />
                      </Form.Group>
                    ) : (
                      <Form.Group className="mb-3">
                        <Form.Label>Room for this occurrence</Form.Label>
                        <Form.Select
                          value={rescheduleClassroomId}
                          onChange={(e) => setRescheduleClassroomId(e.target.value)}
                        >
                          <option value="">
                            Use event default
                            {eventData?.classroom_id != null && eventData?.classroom_id !== ""
                              ? ` (${getRoomCode(eventData.classroom_id)})`
                              : ""}
                          </option>
                          {(options.classrooms || []).map((r) => (
                            <option key={r.id} value={String(r.id)}>
                              {r.room_code}
                              {r.building ? ` · ${r.building}` : ""}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    )}
                  </>
                )}
                {!isUnitEventType && (
                  <Form.Group className="mb-3">
                    <Form.Label>Room for this occurrence</Form.Label>
                    <Form.Select
                      value={rescheduleClassroomId}
                      onChange={(e) => setRescheduleClassroomId(e.target.value)}
                    >
                      <option value="">
                        Use event default
                        {eventData?.classroom_id != null && eventData?.classroom_id !== ""
                          ? ` (${getRoomCode(eventData.classroom_id)})`
                          : ""}
                      </option>
                      {(options.classrooms || []).map((r) => (
                        <option key={r.id} value={String(r.id)}>
                          {r.room_code}
                          {r.building ? ` · ${r.building}` : ""}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                )}
                <Form.Group className="mb-3">
                  <Form.Label>Zoom / online link (whole event)</Form.Label>
                  <Form.Control
                    type="url"
                    placeholder="https://…"
                    value={rescheduleMeetingLink}
                    onChange={(e) => setRescheduleMeetingLink(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Applies to all dates of this session event unless a unit slot has its own online link above.
                  </Form.Text>
                </Form.Group>
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRescheduleScheduled(null)} disabled={rescheduleSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleRescheduleSave} disabled={rescheduleSaving || !rescheduleStart || !rescheduleEnd}>
            {rescheduleSaving ? <Spinner animation="border" size="sm" className="me-1" /> : null}
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
